import { ProtectRole } from "@/components/ProtectRole";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROLES } from "@/config/roles";
import { useAuth } from "@/hooks/use-auth";
import { useBreakoutWidth } from "@/hooks/use-breakout-width";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, Check, FileCheck2, FileText, Info } from "lucide-react";
import { Fragment, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";
import { postValidatePelaksanaanPendidikan } from "../api/post-validate-pelaksanaan-pendidikan";
import { postValidateUnsurMembimbingSeminarProposal } from "../api/post-validate-unsur-membimbing-seminar-proposal";
import { postValidateUnsurMembimbingTa } from "../api/post-validate-unsur-membimbing-ta";
import { postValidateUnsurMengujiProposal } from "../api/post-validate-unsur-menguji-proposal";
import { postValidateUnsurMengujiTa } from "../api/post-validate-unsur-menguji-ta";
import { postValidateUnsurPembimbingAkademik } from "../api/post-validate-unsur-pembimbing-akademik";
import { postValidateUnsurPenelitian } from "../api/post-validate-unsur-penelitian";
import { postValidateUnsurPenunjang } from "../api/post-validate-unsur-penunjang";
import { putPilihIku } from "../api/put-pilih-iku";
import { TabelIku } from "./TabelIku";

export const DISABLE_KLAIM_IKU_7: string[] = [
  // "REN234",
  // "REN238",
  // "REN240",
  // "REN242",
  // "REN244",
  // "REN247",
  // "REN249",
  // "REN261",
  // "REN263",
  // "REN265",
  // "REN267",
  // "REN269",
  // "REN271",
  // "REN273",
  // "REN275",
  // "REN277",
  // "REN279",
  // "REN281",
  // "REN283",
  // "REN285",
  // "REN287",
  // "REN289",
  // "REN291",
  // "REN293",
  // "REN295",
  // "REN297",
  // "REN299",
];

const KlaimIkuDialogContent = ({
  row,
  statusSekarang,
  claimMutation,
  semester,
  year,
  niu,
  periodeId,
  sisaSks,
  month,
}: any) => {
  const [draggedProduct, _setDraggedProduct] = useState<any>(null);
  const [clickedProduct, setClickedProduct] = useState<any>(null);

  const handleClaim = (product: any, selectedIku: string | null) => {
    const isCancel = selectedIku === null;

    let finalDetails: any[] = [];

    const previouslyClaimed = row.validationData.filter((d: any) => d.is_taken);

    if (!isCancel && sisaSks !== undefined) {
      if (sisaSks - row.converted_to_sks < 0) {
        toast.error("Sisa SKS tidak mencukupi untuk klaim IKU ini.");
        return;
      }
    }

    if (isCancel) {
      const remainingClaimed = previouslyClaimed.filter(
        (d: any) => d.url !== product.url,
      );
      finalDetails = remainingClaimed.map((d: any) => ({
        detail: d.url,
        transfer_to: d.iku_terpilih,
      }));
    } else {
      const remainingClaimed = previouslyClaimed.filter(
        (d: any) => d.url !== product.url,
      );
      finalDetails = [
        ...remainingClaimed.map((d: any) => ({
          detail: d.url,
          transfer_to: d.iku_terpilih,
        })),
        { detail: product.url, transfer_to: selectedIku },
      ];
    }

    claimMutation.mutate(
      {
        baseUrlPath: row.isSeminarProposal
          ? "unsur-membimbing-seminar-proposal"
          : row.isPembimbingAkademik
            ? "unsur-pembimbing-akademik"
            : "unsur-penelitian",
        payload: {
          semester: semester?.toString(),
          tahun: year?.toString(),
          selections: {
            [row.kode.replace("ren_", "REN")]: {
              details: finalDetails,
            },
          },
        },
      },
      {
        onSettled: () => {
          setClickedProduct(null);
        },
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Klaim {row.iku}</DialogTitle>
        <p className="text-sm text-slate-500">
          Tarik (drag) produk di sebelah kiri lalu jatuhkan (drop) ke baris IKU
          di sebelah kanan, atau <b>klik salah satu produk</b> untuk memunculkan
          tombol pilih pada tabel.
        </p>
      </DialogHeader>
      <div className="mt-4 flex flex-col md:flex-row gap-6 h-[80vh] overflow-auto">
        {/* Kiri: Daftar Produk */}
        <div className="w-full md:w-[35%] min-w-0 flex flex-col gap-3 overflow-y-auto pr-2 pb-4">
          <h3 className="font-semibold text-slate-700 sticky top-0 bg-white z-10 py-2 border-b border-slate-100 flex items-center justify-between">
            <span>Daftar Karya</span>
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
              {row.validationData.filter((d: any) => d.is_iku).length} Item
            </span>
          </h3>
          {row.validationData
            .filter((d: any) => d.is_iku)
            .map((d: any, idx: number) => (
              <div
                key={idx}
                // draggable={!d.is_taken}
                // onDragStart={(e) => {
                //   setDraggedProduct(d);
                //   e.dataTransfer.setData("application/json", JSON.stringify(d));
                //   // minimal data needed for html5 dnd to work across all browsers
                //   e.dataTransfer.effectAllowed = "move";
                // }}
                // onDragEnd={() => setDraggedProduct(null)}
                // onClick={() => {
                //   if (!d.is_taken) {
                //     setClickedProduct(clickedProduct === d ? null : d);
                //   }
                // }}
                className={`p-4 border rounded-xl bg-white shadow-sm transition-all ${
                  d.is_taken
                    ? "opacity-60 border-slate-200"
                    : clickedProduct === d
                      ? "border-indigo-500 ring-2 ring-indigo-200 shadow-md cursor-pointer"
                      : "cursor-pointer active:cursor-grabbing hover:shadow-md hover:border-indigo-300 border-l-4 border-l-indigo-500"
                }`}
              >
                <div className="flex-1 overflow-hidden">
                  <h4
                    className="font-medium text-sm text-slate-800 line-clamp-2 leading-tight"
                    title={d.sks_judul}
                  >
                    {d.sks_judul}
                  </h4>
                  <p
                    className="text-[11px] text-slate-500 mt-1.5 truncate"
                    title={d.url}
                  >
                    {d.url}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    {d.is_taken ? (
                      <span className="px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-[10px] font-bold border border-green-200 uppercase tracking-wider">
                        Telah diklaim IKU
                      </span>
                    ) : (
                      <div className="flex gap-2">
                        {/* <span className="px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-600 text-[10px] font-bold border border-indigo-200 uppercase tracking-wider flex items-center gap-1">
                          Klik/Tarik untuk mengklaim IKU➔
                        </span> */}
                        {clickedProduct === d && (
                          <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-600 text-[10px] font-bold border border-emerald-200 uppercase tracking-wider flex items-center gap-1">
                            Pilih di Tabel
                          </span>
                        )}
                      </div>
                    )}
                    {d.is_taken && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleClaim(d, null)}
                        // disabled={claimMutation.isPending}
                        disabled={true}
                        className="h-6 text-[10px] px-3 font-semibold rounded-md"
                      >
                        Batalkan
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Kanan: Tabel IKU */}
        <div className="w-full md:w-[65%] min-w-0 overflow-hidden flex flex-col relative">
          <div className="mb-2 flex flex-col">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              Tabel IKU{" "}
              <span className="text-xs font-normal text-slate-500">
                (Area Drop)
              </span>
            </h3>
            {draggedProduct && (
              <p className="text-xs text-indigo-600 font-medium animate-pulse">
                Jatuhkan produk pada baris IKU yang sesuai...
              </p>
            )}
            {clickedProduct && !draggedProduct && (
              <p className="text-xs text-emerald-600 font-medium animate-pulse">
                Pilih IKU untuk produk yang ditandai...
              </p>
            )}
          </div>
          <div
            className={`flex-1 overflow-auto rounded-lg shadow-sm border border-slate-200 bg-slate-50/50 p-2 transition-all ${draggedProduct ? "ring-2 ring-indigo-400" : clickedProduct ? "ring-2 ring-emerald-400" : ""}`}
          >
            <TabelIku
              jabatanFungsional={statusSekarang}
              activeIku={row.iku}
              isClaiming={claimMutation.isPending}
              onDropIku={(selectedIku: string) => {
                if (draggedProduct) {
                  handleClaim(draggedProduct, selectedIku);
                }
              }}
              onClaimIku={
                clickedProduct
                  ? (selectedIku: string | null) =>
                      handleClaim(clickedProduct, selectedIku)
                  : undefined
              }
              niu={niu}
              year={year}
              semester={semester}
              periodeId={periodeId}
              month={month}
              activeProduct={clickedProduct || draggedProduct}
            />
          </div>
        </div>
      </div>
    </>
  );
};

interface TabelPeformaKinerjaProps {
  groupedPerformanceData: any[];
  performanceData: any[];
  year: number | string;
  semester: number | string;
  data: any;
  setSelectedAgendaParams: (params: any) => void;
  setAgendaSheetOpen: (open: boolean) => void;
  periodeId: string | number;
  sisaSks?: number;
  month?: string | null;
  showValidasi?: boolean;
  showNilai?: boolean;
}

export function TabelPeformaKinerja({
  groupedPerformanceData,
  performanceData,
  year,
  semester,
  data,
  setSelectedAgendaParams,
  setAgendaSheetOpen,
  periodeId,
  sisaSks,
  month,
  showValidasi = false,
  showNilai = false,
}: TabelPeformaKinerjaProps) {
  const breakoutStyle = useBreakoutWidth();
  const mappedMonths = Object.entries(data?.data_per_bulan || {}).map(
    ([key, val]) => ({
      month: key,
      ...(val as any),
    }),
  );
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
  const currentMonthData: any =
    mappedMonths.find((m) => m.month === currentMonthKey) ||
    mappedMonths[mappedMonths.length - 1] ||
    {};

  const statusSekarang = currentMonthData?.jabatan_fungsional || "-";

  const { niu } = useParams();
  const { user } = useAuth();
  const currentNiu = niu || user?.niu || "";
  const isDosen = user?.role === "dosen" || user?.role === "DOSEN";
  const queryClient = useQueryClient();
  const [openIkuDialog, setOpenIkuDialog] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const claimMutation = useMutation({
    mutationFn: (data: { baseUrlPath?: string; payload: any }) =>
      putPilihIku(currentNiu, data.payload, data.baseUrlPath),
    onSuccess: () => {
      toast.success("Berhasil Mentransfer Data");
      queryClient.invalidateQueries({
        queryKey: ["unsur-penelitian", currentNiu],
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-membimbing-seminar-proposal", currentNiu],
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-pembimbing-akademik", currentNiu],
      });
      queryClient.invalidateQueries({
        queryKey: ["iku-3-penelitian"],
      });
      queryClient.invalidateQueries({
        queryKey: ["iku-6-penelitian"],
      });
      queryClient.invalidateQueries({
        queryKey: ["iku-4-penelitian"],
      });
      queryClient.invalidateQueries({
        queryKey: ["iku-5-penelitian"],
      });
      queryClient.invalidateQueries({
        queryKey: ["iku-7-penelitian"],
      });
      queryClient.invalidateQueries({
        queryKey: ["akumulasi-iku"],
      });
    },
    onError: () => {
      toast.error("Gagal memilih IKU");
    },
  });

  const validateMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidatePelaksanaanPendidikan({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", {
        id: "validate-pendidikan",
      });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", {
        id: "validate-pendidikan",
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-pelaksanaan-pendidikan", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", { id: "validate-pendidikan" });
    },
  });

  const validateSeminarProposalMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidateUnsurMembimbingSeminarProposal({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", { id: "validate-seminar" });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", { id: "validate-seminar" });
      queryClient.invalidateQueries({
        queryKey: ["unsur-membimbing-seminar-proposal", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", { id: "validate-seminar" });
    },
  });

  const validateMengujiProposalMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidateUnsurMengujiProposal({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", {
        id: "validate-menguji-proposal",
      });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", {
        id: "validate-menguji-proposal",
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-menguji-proposal", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", {
        id: "validate-menguji-proposal",
      });
    },
  });

  const validateMembimbingTaMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidateUnsurMembimbingTa({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", {
        id: "validate-membimbing-ta",
      });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", {
        id: "validate-membimbing-ta",
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-membimbing-ta", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", {
        id: "validate-membimbing-ta",
      });
    },
  });

  const validateMengujiTaMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidateUnsurMengujiTa({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", {
        id: "validate-menguji-ta",
      });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", {
        id: "validate-menguji-ta",
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-menguji-ta", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", {
        id: "validate-menguji-ta",
      });
    },
  });

  const validatePenunjangMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidateUnsurPenunjang({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", {
        id: "validate-penunjang",
      });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", {
        id: "validate-penunjang",
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-penunjang", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", {
        id: "validate-penunjang",
      });
    },
  });

  const validatePembimbingAkademikMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidateUnsurPembimbingAkademik({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", {
        id: "validate-pembimbing-akademik",
      });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", {
        id: "validate-pembimbing-akademik",
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-pembimbing-akademik", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", {
        id: "validate-pembimbing-akademik",
      });
    },
  });

  const validatePenelitianMutation = useMutation({
    mutationFn: (payload: any) =>
      postValidateUnsurPenelitian({
        niu: currentNiu,
        year: Number(year),
        semester: Number(semester),
        is_validator: payload.is_validator,
        payload,
      }),
    onMutate: () => {
      toast.loading("Sedang memproses validasi...", {
        id: "validate-penelitian",
      });
    },
    onSuccess: () => {
      toast.success("Berhasil melakukan validasi", {
        id: "validate-penelitian",
      });
      queryClient.invalidateQueries({
        queryKey: ["unsur-penelitian", currentNiu],
      });
      // Refetch the detail
      queryClient.invalidateQueries({
        queryKey: ["dosen-remun-detail", currentNiu],
      });
    },
    onError: () => {
      toast.error("Gagal melakukan validasi", {
        id: "validate-penelitian",
      });
    },
  });

  const checkCanValidateRow = (row: any, userRole: string | undefined) => {
    const isUnsurPendidikan = [
      "ren_1",
      "ren_2",
      "ren_3",
      "ren_162",
      "ren_226",
      "ren_227",
      "ren_228",
      "ren_229",
    ].includes(row.kode);

    const isInvalidType =
      (!isUnsurPendidikan &&
        !row.isSeminarProposal &&
        !row.isMengujiProposal &&
        !row.isMembimbingTa &&
        !row.isMengujiTa &&
        !row.isPenunjang &&
        !row.isPembimbingAkademik &&
        !row.isPenelitian) ||
      !row.validationData ||
      row.validationData.length === 0;

    if (isInvalidType) return false;

    const details =
      row.validationData?.[0]?.details_parsed?.validator_details || [];
    const dosenMatch = details.find((d: any) =>
      d.level?.toLowerCase().includes("dosen"),
    );
    const isDosenValid = dosenMatch
      ? dosenMatch.is_valid_taken
      : details[0]?.is_valid_taken;

    const fakultasMatch = details.find((d: any) =>
      d.level?.toLowerCase().includes("fakultas"),
    );
    const isFakultasValid = fakultasMatch
      ? fakultasMatch.is_valid_taken
      : details[1]?.is_valid_taken;

    if (userRole === "dosen") {
      return true;
    } else if (userRole === "admin_fakultas") {
      return !!isDosenValid;
    } else if (userRole === "admin_univ") {
      return !!isFakultasValid;
    }

    return false;
  };

  const handleBatchValidate = async (isValidating: boolean) => {
    if (selectedRows.length === 0) return;

    const toastId = toast.loading(
      `Sedang memvalidasi ${selectedRows.length} komponen...`,
    );
    let successCount = 0;
    let errorCount = 0;

    for (const kode of selectedRows) {
      const row = performanceData.find((r: any) => r.kode === kode);
      if (!row) continue;

      const isUnsurPendidikan = [
        "ren_1",
        "ren_2",
        "ren_3",
        "ren_162",
        "ren_226",
        "ren_227",
        "ren_228",
        "ren_229",
      ].includes(row.kode);

      try {
        if (isUnsurPendidikan) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            entries: (row.validationData || [])
              .filter((v: any) => v.mata_kuliah && v.mata_kuliah !== "")
              .map((v: any) => ({
                mk_kode:
                  v.mk_kode ||
                  v.mata_kuliah?.match(/\[(.*?)\]/)?.[1] ||
                  v.mata_kuliah?.split(" - ")[0] ||
                  "",
                kelas: v.kelas || "",
                jenis_kelas: v.jenis_kelas || "REGULER",
              })),
          };
          await validateMutation.mutateAsync(payload);
        } else if (row.isSeminarProposal) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            details: Array.from(
              new Set(
                (row.validationData || [])
                  .map((v: any) => v.raw_details)
                  .filter(Boolean)
                  .flatMap((raw: string) => raw.split("|")),
              ),
            ),
          };
          await validateSeminarProposalMutation.mutateAsync(payload);
        } else if (row.isMengujiProposal) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            details: Array.from(
              new Set(
                (row.validationData || [])
                  .map((v: any) => v.raw_details)
                  .filter(Boolean)
                  .flatMap((raw: string) => raw.split("|")),
              ),
            ),
          };
          await validateMengujiProposalMutation.mutateAsync(payload);
        } else if (row.isMembimbingTa) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            details: Array.from(
              new Set(
                (row.validationData || [])
                  .map((v: any) => v.raw_details)
                  .filter(Boolean)
                  .flatMap((raw: string) => raw.split("|")),
              ),
            ),
          };
          await validateMembimbingTaMutation.mutateAsync(payload);
        } else if (row.isMengujiTa) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            details: Array.from(
              new Set(
                (row.validationData || [])
                  .map((v: any) => v.raw_details)
                  .filter(Boolean)
                  .flatMap((raw: string) => raw.split("|")),
              ),
            ),
          };
          await validateMengujiTaMutation.mutateAsync(payload);
        } else if (row.isPenunjang) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            details: Array.from(
              new Set(
                (row.validationData || [])
                  .map((v: any) => v.raw_details)
                  .filter(Boolean)
                  .flatMap((raw: string) => raw.split("|")),
              ),
            ),
          };
          await validatePenunjangMutation.mutateAsync(payload);
        } else if (row.isPembimbingAkademik) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            details: Array.from(
              new Set(
                (row.validationData || [])
                  .map((v: any) => v.raw_details)
                  .filter(Boolean)
                  .flatMap((raw: string) => raw.split("|")),
              ),
            ),
          };
          await validatePembimbingAkademikMutation.mutateAsync(payload);
        } else if (row.isPenelitian) {
          const payload = {
            kode_ren: row.kode.replace("ren_", "REN").toUpperCase(),
            is_validator: isValidating,
            details: Array.from(
              new Set(
                (row.validationData || [])
                  .map((v: any) => v.raw_details)
                  .filter(Boolean)
                  .flatMap((raw: string) => raw.split("|")),
              ),
            ),
          };
          await validatePenelitianMutation.mutateAsync(payload);
        }
        successCount++;
      } catch (e) {
        errorCount++;
      }
    }

    if (errorCount === 0) {
      toast.success(`Berhasil memvalidasi ${successCount} komponen!`, {
        id: toastId,
      });
    } else {
      toast.error(`Selesai. Berhasil: ${successCount}, Gagal: ${errorCount}.`, {
        id: toastId,
      });
    }
    setSelectedRows([]);
  };

  const validatableRows = performanceData.filter((row: any) =>
    checkCanValidateRow(row, user?.role),
  );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(validatableRows.map((row: any) => row.kode));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div
      className="relative **:data-[slot=table-container]:overflow-visible px-4 md:px-6"
      style={breakoutStyle}
    >
      {showValidasi && selectedRows.length > 0 && (
        <div className="bg-indigo-50 border-b border-indigo-100 p-3 flex justify-between items-center sticky top-0 z-30">
          <span className="text-indigo-800 text-sm font-semibold">
            {selectedRows.length} komponen terpilih
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-100"
              onClick={() => handleBatchValidate(false)}
            >
              Batalkan Validasi
            </Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => handleBatchValidate(true)}
            >
              <Check className="w-4 h-4 mr-1" />
              Validasi Terpilih
            </Button>
          </div>
        </div>
      )}
      <Table className="[&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:py-2 text-[13px]">
        <TableHeader className="bg-slate-50/80 backdrop-blur-md sticky top-3 z-20 shadow-sm shadow-slate-200/50 outline outline-slate-200/50 before:absolute before:-top-3 before:inset-x-0 before:h-3 before:bg-slate-50/80 before:backdrop-blur-md">
          <TableRow className="hover:bg-transparent">
            {showValidasi && (
              <TableHead className="w-10 text-center">
                {validatableRows.length > 0 && (
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    checked={
                      selectedRows.length === validatableRows.length &&
                      validatableRows.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
              </TableHead>
            )}
            <TableHead className="w-12 text-center text-slate-600 font-bold">
              No
            </TableHead>
            <TableHead className="w-24 text-slate-600 font-bold">
              Kode
            </TableHead>
            <TableHead className="min-w-75 text-slate-600 font-bold">
              Komponen Kegiatan
            </TableHead>

            {/* <TableHead className="text-center text-slate-600 font-bold min-w-16">
              Maks.
            </TableHead> */}
            <TableHead className="text-center text-slate-600 font-bold">
              Nilai Sumber
            </TableHead>
            <TableHead className="text-center text-slate-600 font-bold">
              *SKS
            </TableHead>
            <TableHead className="text-center text-primary font-bold">
              SKS Diperoleh
            </TableHead>
            {/* <TableHead className="text-center text-red-600 font-bold">
              Pengurang BKD
            </TableHead> */}
            {/* <TableHead className="text-center text-amber-600 font-bold">
              Sisa SKS
            </TableHead> */}
            <TableHead className="text-center text-slate-600 font-bold">
              Index
            </TableHead>
            <TableHead className="text-center text-sky-600 font-bold">
              SKS x Index
            </TableHead>
            <TableHead className="text-center text-slate-600 font-bold">
              Point/SKS
            </TableHead>
            <TableHead className="text-center text-indigo-600 font-bold min-w-24">
              Poin Final
            </TableHead>
            {/* <TableHead className="text-center text-emerald-600 font-bold min-w-24">
              Poin Final BE
            </TableHead> */}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedPerformanceData.length === 0 ||
          performanceData.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={12}
                className="text-center py-8 text-slate-500"
              >
                Data performa kinerja tidak tersedia
              </TableCell>
            </TableRow>
          ) : (
            groupedPerformanceData.map((group, gIdx) => (
              <Fragment key={gIdx}>
                {group.items.length > 0 && (
                  <TableRow
                    id={`unsur-${gIdx}`}
                    className="bg-slate-100 hover:bg-slate-100 border-t-2 border-slate-200 scroll-mt-24"
                  >
                    <TableCell
                      colSpan={showValidasi ? 6 : 5}
                      className="font-bold text-slate-700 py-3 uppercase text-xs tracking-wider"
                    >
                      {group.unsur.nama_unsur}
                    </TableCell>
                    <TableCell className="text-center font-bold text-primary py-3 tabular-nums text-sm bg-slate-200/50">
                      {showNilai
                        ? group.items
                            .reduce(
                              (acc: number, item: any) =>
                                acc + (item.converted_to_sks || 0),
                              0,
                            )
                            .toFixed(2)
                        : "***"}
                    </TableCell>
                    {/* Pengurang BKD */}
                    {/* <TableCell className="text-center font-bold text-red-600 py-3 tabular-nums text-sm bg-slate-200/50">
                      {showNilai
                        ? group.items
                            .reduce(
                              (acc: number, item: any) =>
                                acc + (item.dipotong_bkd || 0),
                              0,
                            )
                            .toFixed(2)
                        : "***"}
                    </TableCell> */}
                    {/* Sisa SKS */}
                    {/* <TableCell className="text-center font-bold text-amber-600 py-3 tabular-nums text-sm bg-slate-200/50">
                      {showNilai
                        ? group.items
                            .reduce(
                              (acc: number, item: any) =>
                                acc +
                                ((item.converted_to_sks || 0) -
                                  (item.dipotong_bkd || 0)),
                              0,
                            )
                            .toFixed(2)
                        : "***"}
                    </TableCell> */}
                    {/* Index */}
                    <TableCell className="bg-slate-200/50"></TableCell>
                    {/* SKS Tertimbang */}
                    <TableCell className="text-center font-bold text-sky-600 py-3 tabular-nums text-sm bg-slate-200/50">
                      {showNilai
                        ? group.items
                            .reduce(
                              (acc: number, item: any) =>
                                acc +
                                ((item.converted_to_sks || 0) -
                                  (item.dipotong_bkd || 0)) *
                                  (item.index || 1),
                              0,
                            )
                            .toFixed(2)
                        : "***"}
                    </TableCell>
                    {/* Pengali */}
                    <TableCell className="bg-slate-200/50"></TableCell>
                    {/* Poin Final (Manual) */}
                    <TableCell className="text-center font-bold text-indigo-600 py-3 tabular-nums text-sm bg-slate-200/50">
                      {showNilai
                        ? group.items
                            .reduce(
                              (acc: number, item: any) =>
                                acc +
                                ((item.converted_to_sks || 0) -
                                  (item.dipotong_bkd || 0)) *
                                  (item.index || 1) *
                                  (item.point_sks_pengali || 0),
                              0,
                            )
                            .toFixed(2)
                        : "***"}
                    </TableCell>
                    {/* Poin Final BE */}
                    {/* <TableCell className="text-center font-bold text-emerald-600 py-3 tabular-nums text-sm bg-slate-200/50">
                      {showNilai
                        ? group.items
                            .reduce(
                              (acc: number, item: any) =>
                                acc + (item.point_hasil_final_bulan || 0),
                              0,
                            )
                            .toFixed(2)
                        : "***"}
                    </TableCell> */}
                    <TableCell colSpan={3}></TableCell>
                  </TableRow>
                )}
                {group.items.map((row: any, i: number) => (
                  <TableRow key={row.kode} className="hover:bg-slate-50/50">
                    {showValidasi && (
                      <TableCell className="text-center">
                        {checkCanValidateRow(row, user?.role) && (
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500 cursor-pointer"
                            checked={selectedRows.includes(row.kode)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRows((prev) => [...prev, row.kode]);
                              } else {
                                setSelectedRows((prev) =>
                                  prev.filter((k) => k !== row.kode),
                                );
                              }
                            }}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-center text-slate-500 font-medium">
                      {i + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Badge
                          variant="default"
                          className="font-mono text-[10px] uppercase shrink-0"
                        >
                          {row.kode.replace("_", "")}
                        </Badge>
                        <div className="flex flex-col items-start gap-1">
                          {(() => {
                            const validSks = (row.validationData || []).filter(
                              (v: any) => !!v.url,
                            );

                            if (validSks.length === 0) return null;

                            return (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-start text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-2 text-xs border border-transparent hover:border-indigo-200 h-6 flex items-center gap-0"
                                  >
                                    <div className="flex items-center gap-1.5 overflow-hidden flex-1">
                                      <FileText className="w-3.5 h-3.5 shrink-0" />
                                      <span className="truncate text-left">
                                        Lihat {row.jenis_bukti || "SK"}
                                      </span>
                                    </div>
                                    <span className="shrink-0 ml-1 text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-semibold">
                                      {validSks.length}
                                    </span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="start"
                                  className="w-64 max-h-64 overflow-y-auto"
                                >
                                  {validSks.map((sk: any, idx: number) => {
                                    // Ensure sk.url_file_path is treated as a string to avoid errors if it's null/undefined
                                    const path =
                                      sk.url_file_path || sk.url || "";
                                    const match = path.match(/(20\d{2})/);

                                    // Use optional chaining (?.) or a logical check to access the first capture group
                                    const tahun =
                                      match?.[1] ||
                                      new Date().getFullYear().toString();

                                    const fullUrl = path.startsWith("http")
                                      ? path
                                      : `https://static1.undiksha.ac.id/shakuntala/${tahun}${
                                          path.startsWith("/")
                                            ? path
                                            : "/" + path
                                        }`;

                                    return row.jenis_bukti === "Eviden" &&
                                      sk.details_parsed ? (
                                      <Dialog key={idx}>
                                        <DialogTrigger asChild>
                                          <DropdownMenuItem
                                            onSelect={(e) => e.preventDefault()}
                                            className={`cursor-pointer text-xs flex items-center justify-between gap-2 ${sk.is_taken ? "bg-green-50/50 text-green-700 font-medium hover:bg-green-100 focus:bg-green-100" : ""}`}
                                          >
                                            <div className="flex items-center gap-2 overflow-hidden">
                                              <FileText
                                                className={`w-3.5 h-3.5 shrink-0 ${sk.is_taken ? "text-green-600" : ""}`}
                                              />
                                              <span className="truncate">
                                                {sk.mata_kuliah ||
                                                  sk.sks_judul ||
                                                  `Eviden ${idx + 1}`}
                                              </span>
                                            </div>
                                            {sk.is_taken && (
                                              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                                Diklaim ke IKU
                                              </span>
                                            )}
                                          </DropdownMenuItem>
                                        </DialogTrigger>
                                        <DialogContent className="sm:max-w-xl">
                                          <DialogHeader>
                                            <DialogTitle className="text-lg">
                                              Detail Eviden
                                            </DialogTitle>
                                          </DialogHeader>
                                          <div className="grid grid-cols-1 gap-3 text-sm mt-2 max-h-[60vh] overflow-y-auto pr-2">
                                            {Object.entries(
                                              sk.details_parsed,
                                            ).map(([k, v]) => {
                                              if (typeof v === "object" || !v)
                                                return null;
                                              return (
                                                <div
                                                  key={k}
                                                  className="flex flex-col border-b border-slate-100 pb-2"
                                                >
                                                  <span className="text-xs text-slate-500 capitalize">
                                                    {k.replace(/_/g, " ")}
                                                  </span>
                                                  {typeof v === "string" &&
                                                  (v.startsWith("http://") ||
                                                    v.startsWith(
                                                      "https://",
                                                    )) ? (
                                                    <a
                                                      href={v}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline wrap-break-word"
                                                    >
                                                      {v}
                                                    </a>
                                                  ) : (
                                                    <span className="font-medium text-slate-800 wrap-break-word">
                                                      {v as string}
                                                    </span>
                                                  )}
                                                </div>
                                              );
                                            })}
                                            <div className="pt-2 flex">
                                              <Button
                                                size="sm"
                                                onClick={() => {
                                                  const staticPath =
                                                    sk.details_parsed[
                                                      "static path"
                                                    ] ||
                                                    sk.details_parsed[
                                                      "static_path"
                                                    ];
                                                  const finalUrl = staticPath
                                                    ? `https://static1.undiksha.ac.id/kinerja/${staticPath.replace(/^\//, "")}`
                                                    : fullUrl;
                                                  window.open(
                                                    finalUrl,
                                                    "_blank",
                                                  );
                                                }}
                                                className="bg-indigo-600 hover:bg-indigo-700"
                                              >
                                                Buka Dokumen Bukti
                                              </Button>
                                            </div>
                                          </div>
                                        </DialogContent>
                                      </Dialog>
                                    ) : (
                                      <DropdownMenuItem
                                        key={idx}
                                        onClick={() => {
                                          window.open(fullUrl, "_blank");
                                        }}
                                        className={`cursor-pointer text-xs flex items-center justify-between gap-2 ${sk.is_taken ? "bg-green-50/50 text-green-700 font-medium hover:bg-green-100 focus:bg-green-100" : ""}`}
                                      >
                                        <div className="flex items-center gap-2 overflow-hidden">
                                          awdwadadw
                                          <FileText
                                            className={`w-3.5 h-3.5 shrink-0 ${sk.is_taken ? "text-green-600" : ""}`}
                                          />
                                          <span className="truncate">
                                            {sk.mata_kuliah ||
                                              sk.sks_judul ||
                                              `SK ${idx + 1}`}
                                          </span>
                                        </div>
                                        {sk.is_taken && (
                                          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                            Diklaim
                                          </span>
                                        )}
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            );
                          })()}
                          {!isDosen &&
                            row.is_iku &&
                            !(
                              row.iku?.trim() === "IKU 7" &&
                              DISABLE_KLAIM_IKU_7.includes(
                                row.kode.replace("_", "").toUpperCase(),
                              )
                            ) && (
                              <ProtectRole allowedRoles={[]}>
                                <Dialog
                                  open={openIkuDialog === row.kode}
                                  onOpenChange={(open) =>
                                    setOpenIkuDialog(open ? row.kode : null)
                                  }
                                >
                                  <DialogTrigger asChild>
                                    {(() => {
                                      const ikuItems = (
                                        row.validationData || []
                                      ).filter((d: any) => d.is_iku);
                                      const isAllClaimed =
                                        ikuItems.length > 0 &&
                                        ikuItems.every((d: any) => d.is_taken);

                                      return (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className={`min-h-6 h-auto w-full justify-between items-center text-[10px] gap-1.5 px-2 py-1 ${
                                            isAllClaimed
                                              ? "text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-700"
                                              : "text-amber-600 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:text-amber-700"
                                          }`}
                                        >
                                          <div className="flex items-center gap-1.5">
                                            <FileCheck2 className="w-3.5 h-3.5" />{" "}
                                            {isAllClaimed
                                              ? "Detail Klaim"
                                              : "Klaim"}{" "}
                                            {row.iku}
                                          </div>
                                          <span
                                            className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${isAllClaimed ? "bg-emerald-100/50 text-emerald-700" : "bg-amber-100/50 text-amber-700"}`}
                                          >
                                            {
                                              ikuItems.filter(
                                                (v: any) => v.is_taken,
                                              ).length
                                            }
                                            /{ikuItems.length} Diklaim
                                          </span>
                                        </Button>
                                      );
                                    })()}
                                  </DialogTrigger>
                                  <DialogContent className="sm:max-w-8xl">
                                    <KlaimIkuDialogContent
                                      row={row}
                                      statusSekarang={statusSekarang}
                                      claimMutation={claimMutation}
                                      semester={semester}
                                      year={year}
                                      niu={currentNiu}
                                      periodeId={periodeId}
                                      sisaSks={sisaSks}
                                      month={month}
                                    />
                                  </DialogContent>
                                </Dialog>
                              </ProtectRole>
                            )}
                          {[
                            "ren_1",
                            "ren_2",
                            "ren_3",
                            "ren_162",
                            "ren_226",
                            "ren_227",
                            "ren_228",
                            "ren_229",
                          ].includes(row.kode) && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-full justify-start text-primary hover:text-primary hover:bg-secondary gap-1.5 px-2 text-xs whitespace-nowrap border border-transparent hover:border-primary/20"
                                >
                                  <FileCheck2 className="w-3.5 h-3.5" /> Detail
                                  SKS
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-7xl w-[95vw] max-w-[95vw] max-h-[90vh]">
                                <DialogHeader>
                                  <DialogTitle className="text-xl">
                                    Detail Data SKS -{" "}
                                    {row.kode.replace("_", "").toUpperCase()}
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="mt-4 rounded-lg border border-slate-200 shadow-sm overflow-hidden **:data-[slot=table-container]:max-h-[60vh] **:data-[slot=table-container]:overflow-auto">
                                  {row.validationData &&
                                  row.validationData.filter(
                                    (v: any) => v.mata_kuliah !== "",
                                  ).length > 0 ? (
                                    (() => {
                                      const filteredValidationData =
                                        row.validationData.filter(
                                          (v: any) => v.mata_kuliah !== "",
                                        );
                                      const totalSksMatkul =
                                        filteredValidationData.reduce(
                                          (acc: number, curr: any) =>
                                            acc +
                                            (Number(curr.sks_matakuliah) || 0),
                                          0,
                                        );
                                      const totalSksDiakui =
                                        filteredValidationData.reduce(
                                          (acc: number, curr: any) =>
                                            acc +
                                            (Number(curr.sks_diakui) || 0),
                                          0,
                                        );

                                      return (
                                        <Table>
                                          <TableHeader className="bg-slate-50 sticky top-0 z-20 shadow-sm outline outline-slate-200">
                                            <TableRow>
                                              <TableHead className="w-12 text-center">
                                                No
                                              </TableHead>
                                              <TableHead className="w-64 min-w-64 max-w-64">
                                                Mata Kuliah
                                              </TableHead>
                                              <TableHead className="text-center">
                                                Kelas
                                              </TableHead>
                                              <TableHead className="text-center min-w-24">
                                                Jml Mhs
                                              </TableHead>
                                              <TableHead className="text-center font-bold text-slate-700">
                                                Pertemuan <br></br>(Dosen/Total)
                                              </TableHead>
                                              <TableHead className="text-center whitespace-nowrap">
                                                SKS Matkul
                                              </TableHead>
                                              <TableHead className="text-center text-primary whitespace-nowrap">
                                                SKS Diakui
                                              </TableHead>
                                              <TableHead className="text-center min-w-28">
                                                Status
                                              </TableHead>
                                              <TableHead className="text-center min-w-24 sticky right-0 bg-slate-50/90 shadow-[-4px_0_10px_rgba(0,0,0,0.05)] border-l border-slate-100/50">
                                                Aksi
                                              </TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {filteredValidationData.map(
                                              (val: any, vIdx: number) => (
                                                <TableRow key={vIdx}>
                                                  <TableCell className="text-center font-medium text-slate-500">
                                                    {vIdx + 1}
                                                  </TableCell>
                                                  <TableCell className="font-medium text-slate-800 w-64 min-w-64 max-w-64 whitespace-normal wrap-break-word">
                                                    {val.mata_kuliah || "-"}
                                                  </TableCell>
                                                  <TableCell className="text-center">
                                                    <Badge
                                                      variant="outline"
                                                      className="font-mono"
                                                    >
                                                      {val.kelas || "-"}
                                                    </Badge>
                                                  </TableCell>
                                                  <TableCell className="text-center tabular-nums">
                                                    {val.jml_mahasiswa}
                                                  </TableCell>
                                                  <TableCell className="text-center tabular-nums text-slate-600">
                                                    <span className="font-semibold">
                                                      {val.pertemuan_dosen}
                                                    </span>{" "}
                                                    / 16
                                                  </TableCell>
                                                  <TableCell className="text-center tabular-nums">
                                                    {val.sks_matakuliah}
                                                  </TableCell>
                                                  <TableCell className="text-center tabular-nums font-bold text-primary bg-secondary/30">
                                                    {val.sks_diakui}
                                                  </TableCell>
                                                  <TableCell className="text-center">
                                                    {val.stt == 1 ? (
                                                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200 w-full justify-center shadow-none">
                                                        Terpenuhi
                                                      </Badge>
                                                    ) : (
                                                      <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-200 w-full justify-center shadow-none">
                                                        Belum Terpenuhi
                                                      </Badge>
                                                    )}
                                                  </TableCell>
                                                  <TableCell className="text-center sticky right-0 bg-white shadow-[-4px_0_10px_rgba(0,0,0,0.05)] border-l border-slate-100">
                                                    <Button
                                                      variant="outline"
                                                      size="sm"
                                                      className="h-7 text-xs px-3 whitespace-nowrap text-primary border-primary/20 hover:bg-secondary hover:text-primary hover:border-primary/30 gap-1.5"
                                                      onClick={() => {
                                                        setSelectedAgendaParams(
                                                          {
                                                            nip:
                                                              (data as any)
                                                                ?.datas?.nip ||
                                                              (data as any)
                                                                ?.nip ||
                                                              "",
                                                            kode_matkul:
                                                              val.mata_kuliah?.match(
                                                                /\[(.*?)\]/,
                                                              )?.[1] ||
                                                              val.mata_kuliah?.split(
                                                                " - ",
                                                              )[0] ||
                                                              val.mata_kuliah,
                                                            tahun: year,
                                                            semester: semester,
                                                            kelas: val.kelas,
                                                            nama_matkul:
                                                              val.mata_kuliah,
                                                          },
                                                        );
                                                        setAgendaSheetOpen(
                                                          true,
                                                        );
                                                      }}
                                                    >
                                                      <Calendar className="w-3.5 h-3.5" />{" "}
                                                      Agenda
                                                    </Button>
                                                  </TableCell>
                                                </TableRow>
                                              ),
                                            )}
                                          </TableBody>
                                          <TableFooter className="sticky bottom-0 z-20 bg-slate-100/95 backdrop-blur-sm shadow-[0_-1px_0_rgba(0,0,0,0.1)] outline outline-slate-200">
                                            <TableRow className="hover:bg-transparent border-none">
                                              <TableCell
                                                colSpan={5}
                                                className="text-right py-3.5 text-slate-700 uppercase tracking-wider text-[11px] font-bold"
                                              >
                                                Total Akumulasi SKS
                                              </TableCell>
                                              <TableCell className="text-center tabular-nums text-slate-900 text-sm font-bold">
                                                {totalSksMatkul}
                                              </TableCell>
                                              <TableCell className="text-center tabular-nums font-bold text-primary bg-secondary/80/60 text-sm">
                                                {totalSksDiakui}
                                              </TableCell>
                                              <TableCell
                                                colSpan={2}
                                              ></TableCell>
                                            </TableRow>
                                          </TableFooter>
                                        </Table>
                                      );
                                    })()
                                  ) : (
                                    <div className="text-center py-12 text-slate-500 bg-slate-50/50 flex flex-col items-center justify-center gap-3">
                                      <FileCheck2 className="w-12 h-12 text-slate-300" />
                                      <p>
                                        Data validasi tidak ditemukan untuk
                                        komponen ini.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-75 whitespace-normal">
                      <div className="font-medium text-slate-700 text-left wrap-break-word">
                        {row.kegiatan}
                      </div>
                    </TableCell>

                    {/* <TableCell className="text-center tabular-nums font-semibold text-slate-600 bg-slate-50/50">
                      {row.nilai_max > 0 ? row.nilai_max.toFixed(2) : "~"}
                    </TableCell> */}

                    <TableCell className="text-center tabular-nums">
                      <span>{row.sumber.toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="text-center tabular-nums">
                      <span className="text-[11px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                        {showNilai
                          ? `${row.point_sks?.toFixed(2) || "0.00"} SKS`
                          : "***"}
                      </span>
                    </TableCell>
                    <TableCell className="text-center tabular-nums text-primary font-bold">
                      <div className="flex items-center justify-center gap-1.5">
                        <span>
                          {showNilai
                            ? row.converted_to_sks?.toFixed(2) || "0.00"
                            : "***"}
                        </span>
                        {(() => {
                          const rowCode = row.kode
                            .replace("_", "")
                            .toUpperCase();
                          const numStr = rowCode.replace("REN", "");
                          const num = parseInt(numStr, 10);
                          const isPenulisTooltip =
                            rowCode.startsWith("REN") &&
                            !isNaN(num) &&
                            num >= 250 &&
                            num <= 279;
                          const isPenelitiTooltip =
                            rowCode.startsWith("REN") &&
                            !isNaN(num) &&
                            ((num >= 233 && num <= 249) ||
                              (num >= 280 && num <= 299));

                          if (isPenulisTooltip) {
                            return (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="w-4 h-4 text-slate-400 hover:text-indigo-600 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="right"
                                    className="max-w-md p-4 bg-white shadow-xl border border-slate-200"
                                  >
                                    <div className="text-[11px] text-slate-700 flex flex-col gap-2 font-normal text-left">
                                      <div>
                                        <strong className="text-slate-900 block mb-1 text-xs">
                                          1. Penulis berjumlah 2 (dua) orang:
                                        </strong>
                                        <ul className="space-y-1">
                                          <li className="flex items-start">
                                            <span className="mr-1.5">a.</span>
                                            <span>
                                              Jika penulis pertama sekaligus
                                              penulis korespondensi memperoleh
                                              AK 60%, dan penulis ke-2 atau
                                              penulis anggota memperoleh AK 40%.
                                            </span>
                                          </li>
                                          <li className="flex items-start">
                                            <span className="mr-1.5">b.</span>
                                            <span>
                                              Jika penulis pertama tidak
                                              sekaligus penulis korespondensi
                                              memperoleh AK 50%, dan penulis
                                              ke-2 sekaligus penulis
                                              korespondensi memperoleh AK 50%.
                                            </span>
                                          </li>
                                        </ul>
                                      </div>
                                      <div>
                                        <strong className="text-slate-900 block mb-1 mt-1 text-xs">
                                          2. Penulis berjumlah 3 (tiga) orang
                                          atau lebih:
                                        </strong>
                                        <ul className="space-y-1">
                                          <li className="flex items-start">
                                            <span className="mr-1.5">a.</span>
                                            <span>
                                              Jika penulis pertama sekaligus
                                              penulis korespondensi memperoleh
                                              AK 60%, dan penulis anggota
                                              lainnya memperoleh AK 40% dibagi
                                              rata sejumlah penulis anggota.
                                            </span>
                                          </li>
                                          <li className="flex items-start">
                                            <span className="mr-1.5">b.</span>
                                            <span>
                                              Jika penulis pertama tidak
                                              sekaligus penulis korespondensi
                                              memperoleh AK 40%, penulis
                                              korespondensi memperoleh AK 40%,
                                              dan penulis anggota lainnya
                                              memperoleh AK 20% dibagi rata.
                                            </span>
                                          </li>
                                        </ul>
                                      </div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }

                          if (isPenelitiTooltip) {
                            return (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="w-4 h-4 text-slate-400 hover:text-indigo-600 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="right"
                                    className="max-w-sm p-4 bg-white shadow-xl border border-slate-200"
                                  >
                                    <div className="text-[11px] text-slate-700 flex flex-col gap-2 font-normal text-left">
                                      <div>
                                        <strong className="text-slate-900 block mb-1 text-xs">
                                          Ketentuan Perhitungan SKS:
                                        </strong>
                                        <p className="leading-relaxed">
                                          Ketua kegiatan memperoleh porsi nilai
                                          sebesar{" "}
                                          <strong className="text-indigo-600">
                                            60%
                                          </strong>{" "}
                                          dari total SKS diakui, sedangkan{" "}
                                          <strong className="text-emerald-600">
                                            40%
                                          </strong>{" "}
                                          sisanya dibagi rata kepada seluruh
                                          anggota kegiatan.
                                        </p>
                                      </div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </TableCell>
                    {/* Pengurang BKD */}
                    {/* <TableCell className="text-center tabular-nums text-red-600 font-bold bg-red-50/50">
                      {showNilai
                        ? row.dipotong_bkd?.toFixed(2) || "0.00"
                        : "***"}
                    </TableCell> */}
                    {/* Sisa SKS */}
                    {/* <TableCell className="text-center tabular-nums text-amber-600 font-bold bg-amber-50/50">
                      {showNilai
                        ? Number(
                            (row.converted_to_sks || 0) -
                              (row.dipotong_bkd || 0),
                          ).toFixed(2)
                        : "***"}
                    </TableCell> */}
                    {/* Index */}
                    <TableCell className="text-center tabular-nums text-slate-600 font-medium bg-slate-50/50">
                      {showNilai ? row.index : "***"}
                    </TableCell>
                    {/* SKS Tertimbang */}
                    <TableCell className="text-center tabular-nums text-sky-600 font-bold bg-sky-50/30">
                      {showNilai
                        ? Number(
                            ((row.converted_to_sks || 0) -
                              (row.dipotong_bkd || 0)) *
                              (row.index || 1),
                          ).toFixed(2)
                        : "***"}
                    </TableCell>
                    {/* Pengali */}
                    <TableCell className="text-center tabular-nums text-slate-600 font-medium bg-slate-50/50">
                      {showNilai
                        ? row.point_sks_pengali?.toFixed(2) || "0.00"
                        : "***"}
                    </TableCell>
                    {/* Poin Final (Manual) */}
                    <TableCell className="text-center tabular-nums text-indigo-600 font-bold bg-indigo-50/30">
                      {showNilai
                        ? Number(
                            ((row.converted_to_sks || 0) -
                              (row.dipotong_bkd || 0)) *
                              (row.index || 1) *
                              (row.point_sks_pengali || 0),
                          ).toFixed(2)
                        : "***"}
                    </TableCell>
                    {/* Poin Final BE */}
                    {/* <TableCell className="text-center tabular-nums text-emerald-600 font-bold bg-emerald-50/30">
                      {showNilai
                        ? row.point_hasil_final_bulan?.toFixed(2) || "0.00"
                        : "***"}
                    </TableCell> */}
                    {/* <TableCell className="text-center tabular-nums">
                      {row.point_sks_pengali.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-center tabular-nums font-bold text-primary bg-secondary/10">
                      {row.isJabatanTambahan ? (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <div className="flex items-center justify-center gap-1.5 cursor-help">
                              <span>0.00</span>
                              <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 transition-colors" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="bg-slate-900 text-white border-slate-800 text-[11px] p-3 max-w-72 leading-relaxed">
                            Poin untuk unsur jabatan tambahan telah
                            diakumulasikan dan diakui secara langsung pada{" "}
                            <strong className="text-emerald-400">
                              Poin Struktural
                            </strong>
                            . Oleh karena itu, point diakui pada komponen
                            kinerja ini di-set menjadi 0 agar tidak terjadi
                            perhitungan ganda.
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        row.point_hasil_final_bulan.toFixed(2)
                      )}
                    </TableCell> */}
                  </TableRow>
                ))}
              </Fragment>
            ))
          )}
        </TableBody>
        <ProtectRole allowedRoles={[ROLES.ADMIN_SUPER]}>
          {performanceData.length > 0 && (
            <TableFooter className="sticky bottom-0 z-20 bg-slate-100 font-bold text-slate-700 shadow-[0_-2px_4px_rgba(0,0,0,0.05)] border-t border-slate-300">
              <TableRow className="hover:bg-transparent border-none">
                <TableCell
                  colSpan={showValidasi ? 4 : 3}
                  className="text-right py-4 uppercase text-xs tracking-wider pr-4"
                >
                  Total Keseluruhan
                </TableCell>
                {/* Nilai Sumber Total */}
                <TableCell className="text-center py-4 tabular-nums font-bold text-sm bg-slate-200/50">
                  {showNilai
                    ? performanceData
                        .reduce(
                          (acc: number, item: any) => acc + (item.sumber || 0),
                          0,
                        )
                        .toFixed(2)
                    : "***"}
                </TableCell>
                {/* *SKS (Empty) */}
                <TableCell className="bg-slate-200/50"></TableCell>
                {/* SKS Diperoleh */}
                <TableCell className="text-center py-4 tabular-nums text-sm bg-slate-200/50">
                  {showNilai
                    ? performanceData
                        .reduce(
                          (acc: number, item: any) =>
                            acc + (item.converted_to_sks || 0),
                          0,
                        )
                        .toFixed(2)
                    : "***"}
                </TableCell>
                {/* Pengurang BKD */}
                {/* <TableCell className="text-center text-red-600 py-4 tabular-nums text-sm bg-slate-200/50">
                {showNilai
                  ? performanceData
                      .reduce(
                        (acc: number, item: any) =>
                          acc + (item.dipotong_bkd || 0),
                        0,
                      )
                      .toFixed(2)
                  : "***"}
              </TableCell> */}
                {/* Sisa SKS */}
                {/* <TableCell className="text-center text-amber-600 py-4 tabular-nums text-sm bg-slate-200/50">
                {showNilai
                  ? performanceData
                      .reduce(
                        (acc: number, item: any) =>
                          acc +
                          ((item.converted_to_sks || 0) -
                            (item.dipotong_bkd || 0)),
                        0,
                      )
                      .toFixed(2)
                  : "***"}
              </TableCell> */}
                {/* Index */}
                <TableCell className="bg-slate-200/50"></TableCell>
                {/* SKS Tertimbang */}
                <TableCell className="text-center font-bold text-sky-600 py-4 tabular-nums text-sm bg-slate-200/50">
                  {showNilai
                    ? performanceData
                        .reduce(
                          (acc: number, item: any) =>
                            acc +
                            ((item.converted_to_sks || 0) -
                              (item.dipotong_bkd || 0)) *
                              (item.index || 1),
                          0,
                        )
                        .toFixed(2)
                    : "***"}
                </TableCell>
                {/* Pengali */}
                <TableCell className="bg-slate-200/50"></TableCell>
                {/* Poin Final (Manual) */}
                <TableCell className="text-center font-bold text-indigo-600 py-4 tabular-nums text-sm bg-slate-200/50">
                  {showNilai
                    ? performanceData
                        .reduce(
                          (acc: number, item: any) =>
                            acc +
                            ((item.converted_to_sks || 0) -
                              (item.dipotong_bkd || 0)) *
                              (item.index || 1) *
                              (item.point_sks_pengali || 0),
                          0,
                        )
                        .toFixed(2)
                    : "***"}
                </TableCell>
                {/* Poin Final BE */}
                {/* <TableCell className="text-center font-bold text-emerald-600 py-4 tabular-nums text-sm bg-slate-200/50">
                {showNilai
                  ? performanceData
                      .reduce(
                        (acc: number, item: any) =>
                          acc + (item.point_hasil_final_bulan || 0),
                        0,
                      )
                      .toFixed(2)
                  : "***"}
              </TableCell> */}
              </TableRow>
            </TableFooter>
          )}
        </ProtectRole>
      </Table>
    </div>
  );
}
